import { API_BASE_URL } from '../config/api.config';

/**
 * Construit une URL API complète en gérant les trailing slashes
 * 
 * Cette fonction centralise la logique de construction des URLs API pour éviter
 * les double slashes qui peuvent survenir quand API_BASE_URL a un trailing slash.
 * 
 * @param endpoint - L'endpoint API (ex: "/tenants/check" ou "tenants/check")
 * @returns L'URL complète (ex: "https://localhost:3978/api/appTeam/tenants/check")
 * @throws Error si API_BASE_URL n'est pas configurée
 * 
 * @example
 * // Avec VITE_API_BASE_URL=https://localhost:3978/api/
 * buildApiUrl('/tenants/check')
 * // Retourne: "https://localhost:3978/api/appTeam/tenants/check"
 * 
 * buildApiUrl('tenants/check')
 * // Retourne: "https://localhost:3978/api/appTeam/tenants/check"
 */
export function buildApiUrl(endpoint: string): string {
  // Valider la configuration
  if (!API_BASE_URL || API_BASE_URL.trim() === '') {
    throw new Error(
      'API Configuration Error: VITE_API_BASE_URL is not configured.\n' +
      'Please set VITE_API_BASE_URL in your .env file or .localConfigs.\n' +
      'Example: https://localhost:3978/api/'
    );
  }

  // Supprimer le trailing slash de API_BASE_URL
  const baseUrl = API_BASE_URL.replace(/\/$/, '');

  // S'assurer que l'endpoint commence par un slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  return `${baseUrl}${normalizedEndpoint}`;
}

/**
 * Construit une URL API avec des paramètres de requête
 * 
 * @param endpoint - L'endpoint API
 * @param params - Objet contenant les paramètres de requête
 * @returns L'URL complète avec les paramètres encodés
 * 
 * @example
 * buildApiUrlWithParams('/tenants/check', { azureTenantId: '123' })
 * // Retourne: "https://localhost:3978/api/appTeam/tenants/check?azureTenantId=123"
 */
export function buildApiUrlWithParams(
  endpoint: string,
  params?: Record<string, string | number | boolean>
): string {
  const url = buildApiUrl(endpoint);

  if (!params || Object.keys(params).length === 0) {
    return url;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  return `${url}?${searchParams.toString()}`;
}
